import React from 'react';

import { Icon } from '@eluve/components';
import { Block } from '@eluve/llm-outputs';
import { DynamicArtifactRenderer } from '@eluve/smart-blocks';

export interface TemplatePreviewArtifactProps {
  blocks: Block[];
  /**
   * When provided, each section shows an "Edit section" button (on
   * hover) that hands the block key back — the workspace scrolls the
   * editor to the matching section.
   */
  onEditSection?: (key: string) => void;
}

/**
 * Preview-panel rendering of generated template output, scoped to the
 * template authoring view: top-level groups render their heading here
 * and indent their children, while all actual block rendering stays
 * delegated to the shared DynamicArtifactRenderer (which itself renders
 * group children flush — intentionally untouched).
 */
/** Staggered entrance so the generated note settles in rather than snapping */
const blockEntranceProps = (index: number) => ({
  className:
    'animate-in fade-in-0 slide-in-from-bottom-2 fill-mode-backwards duration-500',
  style: { animationDelay: `${Math.min(index, 8) * 90}ms` },
});

const EditSectionButton: React.FC<{ onClick: () => void }> = ({ onClick }) => (
  <button
    type="button"
    onClick={onClick}
    className="absolute top-0 right-0 flex items-center gap-1 rounded-md border border-border-primary bg-bg-primary px-2 py-1 text-xs font-medium text-text-secondary opacity-0 shadow-sm transition-opacity group-hover/preview-block:opacity-100 hover:bg-bg-secondary hover:text-text-primary focus-visible:opacity-100 focus-visible:ring-3 focus-visible:ring-ring/50 focus-visible:outline-none"
  >
    <Icon name="Pencil" size="xs" />
    Edit section
  </button>
);

export const TemplatePreviewArtifact: React.FC<
  TemplatePreviewArtifactProps
> = ({ blocks, onEditSection }) => (
  <div className="flex flex-col gap-4">
    {blocks.map((block, index) => {
      const entrance = blockEntranceProps(index);
      const editButton = onEditSection && (
        <EditSectionButton onClick={() => onEditSection(block.key)} />
      );
      return block.type === 'group' && block.label ? (
        <div
          key={block.key}
          style={entrance.style}
          className={`group/preview-block relative flex flex-col gap-2 ${entrance.className}`}
        >
          {/* Mirrors the shared renderer's depth-1 heading treatment */}
          <h1 className="text-xl font-semibold text-text-primary @md:text-2xl">
            {block.label}
          </h1>
          <div className="ml-5">
            {/* Label cleared so the renderer skips its own (flush)
                heading; children keep their depth-2 styling */}
            <DynamicArtifactRenderer
              isReadonly
              blocks={[{ ...block, label: '' }]}
              showActions={false}
            />
          </div>
          {editButton}
        </div>
      ) : (
        <div
          key={block.key}
          style={entrance.style}
          className={`group/preview-block relative ${entrance.className}`}
        >
          <DynamicArtifactRenderer
            isReadonly
            blocks={[block]}
            showActions={false}
          />
          {editButton}
        </div>
      );
    })}
  </div>
);
